import { CreateAgent } from "@bloomstack/apis/agentTypes"
import { Workspace } from "@bloomstack/apis/workspace"
import EventEmitter from "eventemitter3"
import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react"
export type NavigatorRoute = 
  | "agent-library"
  | "agent-list"
  | "agent-view"
  | "agent-edit"
  | "agent-create"
  | "agent-clone"
  | "workspace"
  | "workspace-view"
  | "workspace-create"
  | "workspace-edit"
  | "workspace-clone"
  | "workspace-library"
  | "workspace-settings"
  | "workspace-user-list"
  | "workspace-task-list"
  | "workspace-task-create"
  | "workspace-task-edit"
  | "preview-browser"
  | "admin-logs"
  | "admin-perf-analyzer"
  | "admin-users"
  | "admin-roles"
  | "admin-model-profiles"
  | "admin-plans"

/**
 * NavAgentLibraryRoute represents the route for the agent library.
 * It opens the agent library popup upon request.
 */
export type NavAgentLibraryRoute = {
  route: "agent-library"
}

/**
 * NavAgentViewRoute represents the route for viewing a specific agent.
 * Depending on the current implementation it will either open
 * an agent panel or the agent library to view the agent.
 * It requires an agentId to identify which agent to view.
 */
export type NavAgentViewRoute = {
  route: "agent-view"
  props: {
    /** Agent record ID - use for direct agent lookup */
    agentId?: string
    /** @deprecated Use agentUserProfileId instead. System user ID for legacy lookups. */
    agentUserId?: string
    /** User profile ID - preferred for new code, uses V2 service */
    agentUserProfileId?: string
    /** Hide the Invite button (e.g. agent is already a member of the active workspace) */
    hideInvite?: boolean
  }
}

/**
 * NavAgentEditRoute represents the route for editing an existing agent.
 * It requires an agentId to identify which agent to edit.
 * Optionally, it can also take an agentUserId to specify the user context.
 */
export type NavAgentEditRoute = {
  route: "agent-edit"
  props: {
    /** Agent record ID - use for direct agent lookup */
    agentId?: string
    /** @deprecated Use agentUserProfileId instead. System user ID for legacy lookups. */
    agentUserId?: string
    /** User profile ID - preferred for new code, uses V2 service */
    agentUserProfileId?: string
    /** Hide the Invite button (e.g. agent is already a member of the active workspace) */
    hideInvite?: boolean
  }
}

/**
 * NavAgentListRoute represents the route for listing all agents.
 * It opens the agent list panel upon request.
 */
export type NavAgentListRoute = {
  route: "agent-list"
}

/**
 * NavAgentCreateRoute represents the route for creating a new agent.
 * It opens the agent creation panel and can take partial properties
 * of CreateAgent to pre-fill the form.
 */
export type NavAgentCreateRoute = {
  route: "agent-create"
  props: Partial<CreateAgent>
}

/**
 * NavAgentCloneRoute represents the route for cloning an existing agent.
 * It creates a copy of the agent and selects the new clone in the sidebar list.
 */
export type NavAgentCloneRoute = {
  route: "agent-clone"
  props: {
    agentId: string
  }
}

/**
 * NavWorkspaceLibraryRoute represents the route for the workspace library.
 * It opens the workspace library popup upon request.
 */
export type NavWorkspaceLibraryRoute = {
  route: "workspace-library"
}

/**
 * NavWorkspaceViewRoute represents the route for viewing a specific workspace.
 * It requires a workspaceId to identify which workspace to view.
 */
export type NavWorkspaceViewRoute = {
  route: "workspace-view"
  props: {
    workspaceId: string
  }
}

/**
 * NavWorkspaceCreateRoute represents the route for creating a new workspace.
 * It opens the workspace creation panel upon request.
 */
export type NavWorkspaceCreateRoute = {
  route: "workspace-create"
}

/**
 * NavWorkspaceEditRoute represents the route for editing an existing workspace.
 * It requires a workspaceId to identify which workspace to edit.
 */
export type NavWorkspaceEditRoute = {
  route: "workspace-edit"
  props: {
    workspaceId: string
  }
}

/**
 * NavWorkspaceCloneRoute represents the route for cloning an existing workspace.
 * It requires a workspaceId to identify which workspace to clone.
 */
export type NavWorkspaceCloneRoute = {
  route: "workspace-clone"
  props: {
    workspaceId: string
  }
}

/**
 * NavPreviewBrowserRoute represents the route for opening an HTML file
 * in the workspace's preview browser tab. If the tab is already open,
 * its content swaps to the new file. If not, the tab opens with it.
 */
export type NavPreviewBrowserRoute = {
  route: "preview-browser"
  props: {
    /** Workspace that owns the file */
    workspaceId: string
    /** DB filePath including workspaceId prefix (e.g. "abc123/docs/index.html") */
    filePath: string
  }
}

export type NavAdminLogsRoute = { route: "admin-logs" }
export type NavAdminPerfAnalyzerRoute = { route: "admin-perf-analyzer" }
export type NavAdminUsersRoute = { route: "admin-users" }
export type NavAdminRolesRoute = { route: "admin-roles" }
export type NavAdminModelProfilesRoute = { route: "admin-model-profiles" }
export type NavAdminPlansRoute = { route: "admin-plans" }

export type NavIntegrationMcpCreateRoute = {
  route: "integration-mcp-create";
  props: {
    serverUrl?: string;
    namespace?: string;
  };
}

export type NavigatorRouteProps = 
  | { // general routes
    route: NavigatorRoute
    props: Record<string, any>
  }

  // agent panel routes
  | NavAgentListRoute

  // agent routes
  | NavAgentViewRoute
  | NavAgentCreateRoute
  | NavAgentEditRoute
  | NavAgentCloneRoute
  | NavAgentLibraryRoute

  // workspace routes
  | NavWorkspaceLibraryRoute
  | NavWorkspaceViewRoute
  | NavWorkspaceCreateRoute
  | NavWorkspaceEditRoute
  | NavWorkspaceCloneRoute

  // preview browser routes
  | NavPreviewBrowserRoute

  // admin routes
  | NavAdminLogsRoute
  | NavAdminPerfAnalyzerRoute
  | NavAdminUsersRoute
  | NavAdminRolesRoute
  | NavAdminModelProfilesRoute
  | NavAdminPlansRoute

  // integration routes
  | NavIntegrationMcpCreateRoute

  | {
  route: "workspace-settings"
  props: {
    workspaceId: string
  }
} | {
  route: "workspace-task-list"
  props: {
    workspaceId: string
  }
} | {
  route: "workspace-task-create"
  props: {
    workspaceId: string
  }
} | {
  route: "workspace-task-edit"
  props: {
    workspaceId: string
    taskId: string
  }
} | {
  route: "workspace"
  props: {
    workspace: Workspace
  }
} | {
  route: "workspace-user-list"
  props: {
    workspaceId: string
  }
}

export type NavigatorContextType = {
  open(route: NavigatorRouteProps): void
  subscribe(onChange: (route: NavigatorRouteProps) => void): () => void
  clearRoute(): void
}

export const NavigatorContext = createContext<NavigatorContextType>({
  open: () => {},
  subscribe: () => () => {},
  clearRoute: () => {}
});

export const useNavigatorApi = () => {
  const context = useContext(NavigatorContext)
  if (!context) {
    throw new Error("useNavigator must be used within a NavigatorProvider")
  }

  return context
}

export const useNavigatorDriver = (onChange: (route: NavigatorRouteProps) => void, deps: any[] = []) => {
  const context = useNavigatorApi()

  useEffect(() => {
    const unsubscribe = context.subscribe(onChange)
    return () => unsubscribe()    
  }, [context, onChange, ...deps])
}

export type NavigatorProviderProps = {
  children: ReactNode | null
}

export const NavigatorProvider: FC<NavigatorProviderProps> = ({ children }) => {
  const subscribersRef = useRef<((route: NavigatorRouteProps) => void)[]>([])
  const routeRef = useRef<NavigatorRouteProps | null>(null)
  const routeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const api: NavigatorContextType = useMemo(() => ({
    open: (route: NavigatorRouteProps) => {
      routeRef.current = route
      subscribersRef.current.forEach(subscriber => {
        try {
          subscriber(route)
        } catch (error) {
          console.error("Error in navigator subscriber", error)
        }
      });

      if (routeTimeoutRef.current) {
        clearTimeout(routeTimeoutRef.current)
      }

      routeTimeoutRef.current = setTimeout(() => {
        routeRef.current = null
      }, 10000); // Clear route after 10 seconds
    },
    subscribe: (onChange: (route: NavigatorRouteProps) => void) => {
      subscribersRef.current.push(onChange)
      if (routeRef.current) {
        onChange(routeRef.current)
      }
      return () => {
        const index = subscribersRef.current.indexOf(onChange)
        if (index >= 0) {
          subscribersRef.current.splice(index, 1)
        }
      }
    },
    clearRoute: () => {
      routeRef.current = null;
    }
  }), [])

  return (
    <NavigatorContext.Provider value={api}>
      {children}
    </NavigatorContext.Provider>
  )
}